import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import ValidationErrors from '@/components/validation-errors';
import type { Company } from '@/types/company';
import type { Project } from '@/types/project';
import { Form } from '@inertiajs/react';
import { useState } from 'react';

interface Props {
  project: Pick<Project, 'name' | 'endState' | 'companyId'> & {
    id: string | null;
    singleShot?: boolean;
  };
  companies: Company[];
  action: string;
  method: 'post' | 'patch';
  submitLabel: string;
}

export default function ProjectForm({ project, companies, action, method, submitLabel }: Props) {
  const [companyId, setCompanyId] = useState(project.companyId ?? companies[0]?.id ?? '');
  const [singleShot, setSingleShot] = useState(project.singleShot ?? false);

  return (
    <Form method={method} action={action} className="space-y-6">
      {({ errors, processing }) => (
        <>
          <div className="space-y-2">
            <Label htmlFor="project_name">Name</Label>
            <Input
              id="project_name"
              name="project[name]"
              defaultValue={project.name ?? ''}
              autoFocus
              required
            />
            <ValidationErrors errors={errors.name} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="project_end_state">End state</Label>
            <Textarea
              id="project_end_state"
              name="project[end_state]"
              defaultValue={project.endState ?? ''}
              required
              rows={3}
            />
            <ValidationErrors errors={errors.endState} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="project_company_id">Company</Label>
            <input type="hidden" name="project[company_id]" value={companyId} />
            <Select value={companyId} onValueChange={setCompanyId}>
              <SelectTrigger id="project_company_id" className="w-full">
                <SelectValue placeholder="Select company" />
              </SelectTrigger>
              <SelectContent>
                {companies.map((company) => (
                  <SelectItem key={company.id} value={company.id}>
                    {company.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <ValidationErrors errors={errors.companyId} />
          </div>

          <div className="flex items-start gap-3">
            <input type="hidden" name="project[single_shot]" value={singleShot ? '1' : '0'} />
            <Checkbox
              id="project_single_shot"
              checked={singleShot}
              onCheckedChange={(checked) => setSingleShot(checked === true)}
              className="mt-1"
            />
            <div className="space-y-1">
              <Label htmlFor="project_single_shot">Single-shot project</Label>
              <p className="text-muted-foreground text-sm">
                One-checkbox roadmap; auto-archives when complete.
              </p>
            </div>
          </div>

          <Button type="submit" disabled={processing}>
            {submitLabel}
          </Button>
        </>
      )}
    </Form>
  );
}
